import { ChangeDetectorRef, Component, inject, OnInit, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { AuthService } from '../../_core/auth.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { ILoginData, IResponse } from '../../core.interface';
import { MessageModule } from 'primeng/message';
import { environment } from '../../../environments/environment';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Spinner } from '../../_core/spinner';
import { BrandingService } from '../../_core/branding.service';
import { AppBuildInfoService } from '../../_core/app-build-info.service';
import {
  FIXED_NETWORKS_OFFICE_URL,
  PREVIOUS_VIRTUAL_OFFICE_URL,
  PROPERTY_MANAGERS_OFFICE_URL,
} from '../../welcome-tour/previous-office-url';

@Component({
  selector: 'app-login',
  imports: [
    ButtonModule,
    CardModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    FloatLabelModule,
    MessageModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  protected navigate = output<string>();

  readonly title = inject(Title).setTitle('Oficina Virtual - Inicio de sesión');
  private $auth = inject(AuthService);
  private $spinner = inject(Spinner);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private aRoute = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  public $branding = inject(BrandingService);
  protected readonly buildInfo = inject(AppBuildInfoService);
  protected readonly previousOfficeUrl = PREVIOUS_VIRTUAL_OFFICE_URL;
  protected readonly propertyManagersOfficeUrl = PROPERTY_MANAGERS_OFFICE_URL;
  protected readonly fixedNetworksOfficeUrl = FIXED_NETWORKS_OFFICE_URL;

  public form: FormGroup = this.initForm();
  public loginError: string = '';

  public isLoading: boolean = false;
  public showPassword = false;

  public isProduction: boolean = environment.production;

  ngOnInit(): void {
    this.aRoute.queryParams.subscribe((params) => {
      if (params['error']) {
        this.loginError =
          params['error'] === 'data'
            ? 'No se pudo recuperar la sesión. Por favor, inicia sesión de nuevo.'
            : params['error'];
      }
    });
  }

  public submit(): void {
    const DATA = this.form.value;
    this.isLoading = true;
    this.loginError = '';
    this.$spinner.show();
    this.$auth
      .signin(DATA)
      .pipe(
        tap((response: IResponse<ILoginData>) => {
          if (response.data.expiredPassword === true) {
            this.$spinner.hide();
            this.navigateTo('CHANGE_PASSWORD');
          } else {
            this.loadingUser();
          }
        }),
        catchError((err: { error?: { message?: string } }) => {
          this.$spinner.hide();
          this.isLoading = false;
          this.loginError = err.error?.message ?? 'Error de autenticación';
          return of(null);
        }),
        finalize(() => {
          this.cdr.detectChanges();
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  private async loadingUser(): Promise<void> {
    const destination = await this.$auth.completeLoginFlow();
    this.isLoading = false;
    if (destination === 'CHANGE_PASSWORD') {
      void this.router.navigate(['/', 'change-password'], { replaceUrl: true });
    } else if (destination === 'LOADING') {
      this.router.navigate(['/', 'loading']);
    } else if (destination === 'PROPIEDADES') {
      this.router.navigate(['/', 'propiedades']);
    }
    this.$spinner.hide();
  }

  private initForm(): FormGroup {
    return this.formBuilder.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
      appId: [environment.appID, Validators.required],
    });
  }

  public navigateTo(view: 'REGISTER' | 'CHANGE_PASSWORD' | 'RECOVERY_PASSWORD'): void {
    this.navigate.emit(view);
  }
}
